import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, type Competition, type Role } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { canRegisterClan } from '../lib/roles';
import { useEffectiveMe, useViewAs } from '../lib/viewAs';
import { SETTLE, settleDelay } from '../lib/motion';
import { cn } from '../lib/cn';
import { GateShell, GatePill, GateNote, friendlyGateError } from './gate/ui';

// ─────────────────────────────────────────────────────────────────────────────
// COMPETITION AS A CLAN — GATE flow (Joining → Competition as a clan). Your
// clan, one tap per open competition. Wraps POST /competitions/:id/
// register-my-clan (leader-gated server-side; immediate REGISTERED).
//
// Terms/rules acceptance step (comp-terms-acceptance): register-my-clan 403s
// with terms_not_accepted until the clan's leader has ANY acceptance on file
// — durable (Lisa, 2026-07-08 amendment): once accepted, always accepted for
// this comp, regardless of later rules edits. Selecting a comp first checks
// status (getCompTermsAcceptance) — any existing acceptance skips silently
// straight to registration; only a clan with NO acceptance on file sees the
// accept step, which renders the comp's CURRENT rules text inline (plus a
// link to the static rules page + a checkbox), then registration proceeds
// automatically once accepted.
//
// M1 of the multi-team epic (Lisa, 2026-07-10): a clan with named teams
// (CLAN#/METADATA.teamNames) gets an EXTRA tap between picking a comp and the
// terms/register flow above — "which team?" (default: Main team, the legacy
// whole-clan entry, unchanged). A clan with NO named teams skips this step
// entirely — byte-identical to before this feature existed. Captain
// self-entry (their own team only) is server-supported already (see
// portalApi's register-my-clan).
//
// M2 (M1 flag #3 follow-up, 2026-07-10): the "my active captain grants"
// lookup M1 flagged as missing now exists (GET /clans/:name/my-grant) — the
// team-pick step highlights the caller's OWN team (if they hold an active
// captain grant for one) as the primary/default pill, so a captain
// immediately sees the fast path instead of hunting through every team name.
// This is UX surfacing only — the underlying authorization (leader enters
// any team; a captain only their own) is unchanged and already enforced
// server-side regardless of which pill a caller taps.
// ─────────────────────────────────────────────────────────────────────────────

// Exported so other surfaces that offer a "Register my clan" doorway (e.g.
// MyCompDetail's per-comp button) can gate on the SAME definition of
// "joinable" instead of re-deriving their own — single source of truth.
export const JOINABLE_COMP_STATUSES = new Set(['PENDING', 'LIVE']);

// Pure matcher for the `?compId=` deep-link (doorways link here as
// `/gate/join-comp?compId=<id>`): given the leader's already-joinable comps
// and the raw param, returns the matching comp or undefined. Exported
// standalone, with no React/query dependency, so it's unit-testable without
// mounting the full screen (which needs a QueryClient + WorkspaceProvider).
export function matchJoinableCompId<T extends { id: string }>(
  comps: T[],
  compIdParam: string | null | undefined,
): T | undefined {
  if (!compIdParam) return undefined;
  return comps.find(c => c.id === compIdParam);
}

interface AcceptStep {
  compId: string;
  compName: string;
  // The comp's dynamic rules text (from GET terms-acceptance) — '' when the
  // comp has never had rules text written, in which case the accept step
  // falls back to a note that the org's standard terms apply.
  rules: string;
}

interface TeamPickStep {
  compId: string;
  compName: string;
}

interface RegisterArgs {
  compId: string;
  teamNo?: number;
}

// Single authority for a competition's caption line — shared by the tappable
// pill list and the read-only CompInfoRow below so the two can't drift.
// registrationOpen is server-derived (see api.ts's Competition type); undefined
// (an older cached response, or the DEV preview fixtures) defaults to open so
// nothing regresses for those.
export function compCaption(c: Pick<Competition, 'status' | 'registrationOpen' | 'startDate'>): string {
  if (c.registrationOpen === false) return `Registration closed${c.startDate ? ` ${c.startDate}` : ''}`;
  return c.status === 'LIVE' ? 'Live now' : 'Signups open';
}

// ── Who may actually enter a clan into a competition ────────────────────────
// Mirrors the backend gate at portalApi/index.ts's register-my-clan EXACTLY:
//   • the clan leader — platform role 'leader', which getRole() stamps for
//     BOTH a CLAN_OWNER and a CLAN_LEADER/co-admin membership row,
//   • a founder — the handler's explicit `role !== 'founder'` bypass,
//   • the captain of a NAMED team, entering THAT team only — an active
//     delegation grant carrying a teamNo.
// Everyone else 403s ("Only the clan leader can register the clan"), so the
// tappable pill list must not be offered to them.
//
// Note a captain of a clan with NO named teams correctly falls OUT of this
// set: a team entry needs a teamNo, and a whole-clan entry stays leader-only,
// so there is nothing they could tap. A captain grant is an OVERLAY — it never
// touches the base membership role (shared/delegation.ts) — which is exactly
// why the platform-role check alone can't answer this question.
//
// Pure + exported so it's unit-testable without mounting the screen (which
// needs a QueryClient + WorkspaceProvider) — same precedent as
// matchJoinableCompId above.
export function canEnterCompAsClan(args: {
  role: Role | undefined;
  clanName: string | undefined;
  /** teamNo of the caller's active captain grant, null when they hold none. */
  myTeamNo: number | null;
  /** DEV ?as=<persona> preview. Answered by persona rather than bypassed:
   *  the preview exists to show what each role ACTUALLY sees, so a `member`
   *  or `orgadmin` preview must land on the read-only branch exactly as a
   *  real one does — otherwise the one tool for eyeballing this branch can't
   *  reach it. Mirrors gate/gateIdentity.ts's previewIdentity personas:
   *  leader/founder enter; member/orgadmin don't; public has no clan at all
   *  and never reaches this gate (the clanless branch owns them). */
  preview?: string | null;
}): boolean {
  if (args.preview) return args.preview === 'leader' || args.preview === 'founder';
  if (canRegisterClan(args.role, args.clanName)) return true;
  if (args.role === 'founder' && !!args.clanName) return true;
  return args.myTeamNo != null;
}

// Read-only competition row for the "you can't enter, but here's what's on"
// branch below. Deliberately NOT a disabled GatePill — a greyed-out button
// reads as "broken/closed" and dead-ends the newcomer this screen is meant to
// pull in (Lisa, 2026-07-22). Same raised-panel language as the accept step's
// rules block, so it reads as information rather than a refused action.
function CompInfoRow({ name, caption, idx }: { name: string; caption: string; idx: number }) {
  return (
    <div
      className={cn(SETTLE, 'w-full rounded-2xl bg-white/5 px-6 py-3.5 text-center')}
      style={settleDelay(idx)}
    >
      <p className="font-display text-[14px] font-semibold uppercase tracking-wide text-white/85">{name}</p>
      <p className="mt-0.5 font-sans text-[10px] font-medium text-white/40">{caption}</p>
    </div>
  );
}

export function JoinCompScreen() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const preview = import.meta.env.DEV ? params.get('as') : null;
  // Doorway deep-link (item 3/5 of the registration-hole closure): per-comp
  // and public-page "Register my clan" buttons land here as
  // `?compId=<COMP#...>` — auto-select handling lives in the effect below.
  const compIdParam = params.get('compId');

  const { visibleComps, isLoading: compsLoading } = useWorkspace();
  // The founder VIEW-AS identity leg. `rawMe` is the real caller — the server
  // answers GET /me against the real token and never role-filters it for a
  // preview, so it always describes the founder. Every read on this screen
  // goes through the sanitised local below instead: it is deliberately the one
  // named `me`, so a future `me?.something` cannot reintroduce a raw read the
  // way an `effectiveMe` alongside a live `me` invites.
  //
  // This screen matters more than most. canEnterCompAsClan is the ONLY gate in
  // front of the tappable comp pills, and tapping one runs
  // getCompTermsAcceptance -> acceptCompTerms -> api.registerMyClanInComp: a
  // real, immediate REGISTERED write entering a real clan into a real
  // competition on the founder's real authority. Its `role === 'founder'`
  // branch read the raw role, so every preview kept the founder bypass that
  // the backend handler itself only grants a genuine founder — while the UI
  // told the founder they were previewing a role that cannot do this.
  //
  // Identity no-op whenever no view-as is active (see lib/viewAs.ts's identity
  // guarantee), so real leaders, captains and non-previewing founders are
  // untouched.
  const { data: rawMe, isLoading: meLoading } = useQuery({ queryKey: ['me'], queryFn: api.me, enabled: !preview });
  const me = useEffectiveMe(rawMe);
  const { viewAs } = useViewAs();
  const [done, setDone] = useState<{ comp: string } | null>(null);
  const [teamPickStep, setTeamPickStep] = useState<TeamPickStep | null>(null);
  const [selectedTeamNo, setSelectedTeamNo] = useState<number | undefined>(undefined);
  const [acceptStep, setAcceptStep] = useState<AcceptStep | null>(null);
  const [termsChecked, setTermsChecked] = useState(false);
  // Guards the compId auto-select (below) to a single attempt per mount —
  // once true, the effect is inert no matter how its other deps change (e.g.
  // teamPickStep/acceptStep toggling as the flow advances), so backing out
  // of the auto-selected flow correctly lands on the ordinary full picker
  // instead of re-triggering.
  const [autoSelectDone, setAutoSelectDone] = useState(false);

  // DEV preview: fake clan + comps so the layout can be eyeballed without a backend.
  const clanName = preview ? (preview === 'public' ? undefined : 'Preview Wolves') : me?.clanName;

  // Named-teams lookup for the "which team?" step — only fires once we know
  // the caller's own clan name; a clan with no teamNames renders no step at
  // all (teamOptions.length === 0 below), so this is silent no-op coexistence
  // for every clan that hasn't adopted named teams yet.
  const { data: myClanDetail, isLoading: clanDetailLoading } = useQuery({
    queryKey: ['myClanForJoin', clanName],
    queryFn: () => api.clan(clanName!),
    enabled: !!clanName && !preview,
  });
  const namedTeams = Object.entries(myClanDetail?.teamNames ?? {}).sort(([a], [b]) => Number(a) - Number(b));

  // "Am I an active captain, and of which team." Reads /me's `captaincies`
  // (F1, 2026-07-22) rather than the old per-clan GET /clans/:name/my-grant
  // round-trip: /me is already loaded on every page, both sides run the SAME
  // isGrantActive authority (shared/delegation.ts), and — decisively — the
  // GATE tile that routes people here now gates on that same field, so
  // deriving this from anything else could offer a captain a door onto a
  // screen that then disagreed about who they are.
  //
  // The CAPTAIN leg — the second grant this screen reads, and the one the
  // identity leg above does NOT close on its own. For a platform-role preview
  // applyViewAs zeroes `captaincies`, so the effective `me` is already clean.
  // For a CLAN-SCOPED captain preview it does the opposite: it deliberately
  // MANUFACTURES `captaincies: [{ clanName, teamNo }]` so the persona's own
  // screens render. That manufactured grant would arrive here as a live
  // myTeamNo, unlock the pill list, and let a founder register a real clan's
  // team — possibly one they do not captain at all — into a real competition
  // on their real token.
  //
  // Fail-closed under ANY active preview, the same ruling VetoPage,
  // MatchCardLive and LeaderPanel made for the same leg (LeaderPanel's
  // viewerCaptainTeams; PublicMatchCard.tsx's viewerCaptainOf). A preview is
  // for SEEING what a persona sees; this is a write, and a captain preview
  // that cannot reach the register flow is a far cheaper cost than one that
  // can. Identity no-op with no view-as active, so a real captain keeps their
  // entry path and their "Your team" fast path in full.
  const captaincies = viewAs ? [] : (me?.captaincies ?? []);
  const myCaptaincy = captaincies.find(c => c.clanName === clanName) ?? null;
  const myTeamNo = myCaptaincy?.teamNo ?? null;

  // A captain's OWN team must be enterable even when the clan never NAMED it —
  // teamName is optional at mint time (shared/delegation.ts:169), so a captain
  // of an unnamed team would otherwise reach this screen (the gate lets them)
  // and find no pill for the only team they're allowed to enter. Union it in;
  // leaders hold no captaincy, so their picker is untouched.
  const teamOptions: [string, string][] =
    myCaptaincy && !namedTeams.some(([n]) => Number(n) === myCaptaincy.teamNo)
      ? [...namedTeams, [String(myCaptaincy.teamNo), myCaptaincy.teamName ?? `Team ${myCaptaincy.teamNo}`] as [string, string]]
          .sort(([a], [b]) => Number(a) - Number(b))
      : namedTeams;

  // See canEnterCompAsClan above for the full rule (mirrors register-my-clan).
  const canEnterComp = canEnterCompAsClan({
    role: me?.role,
    clanName,
    myTeamNo,
    preview,
  });

  const registerMut = useMutation({
    mutationFn: ({ compId, teamNo }: RegisterArgs) => api.registerMyClanInComp(compId, teamNo),
    onSuccess: (_res, { compId }) => {
      const comp = comps.find(c => c.id === compId);
      setDone({ comp: comp?.name ?? compId.replace('COMP#', '') });
    },
  });

  // Selecting a comp checks terms status first — ANY existing acceptance
  // (durable, any rulesVersion) skips straight to registration; only a
  // genuinely missing acceptance opens the accept step below. Fires AFTER
  // the team-pick step (if any) — teamNo travels via selectedTeamNo, terms
  // acceptance itself stays clan-scoped regardless of which team is entered.
  const checkTermsMut = useMutation({
    mutationFn: (compId: string) => api.getCompTermsAcceptance(compId),
    onSuccess: (data, compId) => {
      const mine = data.clans[0];
      if (mine?.status === 'accepted') {
        registerMut.mutate({ compId, teamNo: selectedTeamNo });
        return;
      }
      const comp = comps.find(c => c.id === compId);
      setTermsChecked(false);
      setAcceptStep({
        compId, compName: comp?.name ?? compId.replace('COMP#', ''), rules: data.rules,
      });
    },
  });

  const acceptMut = useMutation({
    mutationFn: (compId: string) => api.acceptCompTerms(compId),
    onSuccess: (_res, compId) => {
      setAcceptStep(null);
      registerMut.mutate({ compId, teamNo: selectedTeamNo });
    },
  });

  const comps: Pick<Competition, 'id' | 'name' | 'status' | 'registrationOpen' | 'startDate'>[] = preview
    ? [
        { id: 'COMP#A', name: 'Winter Invitational', status: 'LIVE', registrationOpen: true, startDate: null as string | null },
        { id: 'COMP#B', name: 'Spring Clash', status: 'PENDING', registrationOpen: true, startDate: null as string | null },
      ]
    : visibleComps.filter(c => JOINABLE_COMP_STATUSES.has(c.status));

  // Tapping a comp pill: clans with named teams pause on "which team?" first;
  // everyone else proceeds exactly as before (straight to the terms check).
  const startJoining = (compId: string, compName: string) => {
    if (teamOptions.length > 0) {
      setSelectedTeamNo(undefined);
      setTeamPickStep({ compId, compName });
      return;
    }
    checkTermsMut.mutate(compId);
  };

  // `?compId=` auto-select: runs once (guarded by autoSelectDone), after real
  // data has loaded, and never in the DEV `?as=` preview (fixed fixture comps,
  // no backend to check terms against). If the param matches one of the
  // leader's own joinable comps, jump straight into its team-pick/terms/
  // register flow — otherwise (absent, unmatched, or no clan) this is a
  // silent no-op and the full picker renders exactly as it did before this
  // feature existed. The param is stripped from the URL either way so
  // back-navigation or a refresh mid-flow never re-triggers it.
  useEffect(() => {
    if (autoSelectDone) return;
    if (preview) { setAutoSelectDone(true); return; }
    if (meLoading || compsLoading) return; // wait for real data before matching
    setAutoSelectDone(true);
    if (compIdParam) {
      const next = new URLSearchParams(params);
      next.delete('compId');
      setParams(next, { replace: true });
    }
    if (!clanName) return; // no clan → the "build a clan first" screen handles it regardless
    // A caller who can't register must never be auto-launched into the
    // terms/register flow by a ?compId= deep link — that's precisely the path
    // that used to 403 them. The read-only branch below handles them instead.
    if (!canEnterComp) return;
    const match = matchJoinableCompId(comps, compIdParam);
    if (match) startJoining(match.id, match.name || match.id.replace('COMP#', ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSelectDone, preview, meLoading, compsLoading, clanName, compIdParam, comps]);

  if (done) {
    return (
      <GateShell header="Registered:" onBack={() => navigate('/gate')}>
        <p className="settle text-center font-display text-xl font-bold uppercase tracking-wide text-white">
          {clanName}
        </p>
        <GateNote idx={1}>is in</GateNote>
        <p className="settle text-center font-display text-xl font-bold uppercase tracking-wide text-white">
          {done.comp}
        </p>
        <GatePill label="Dashboard" onClick={() => navigate('/')} primary idx={2} />
        <GatePill label="Back to gate" onClick={() => navigate('/gate')} idx={3} />
      </GateShell>
    );
  }

  // clanDetailLoading is included so teamOptions is settled before we branch.
  // The captaincy signal itself now rides on `me` (meLoading already covers
  // it), so canEnterComp can no longer resolve late — a captain never flashes
  // the read-only branch before their grant lands. clanDetailLoading resolves
  // to false while its query is disabled (react-query: a disabled query is
  // pending, not loading), so a clanless caller never waits on it.
  if (!preview && (meLoading || compsLoading || clanDetailLoading)) {
    return <GateShell header="Joining:" onBack={() => navigate('/gate')}><GateNote idx={2}>…</GateNote></GateShell>;
  }

  if (!clanName) {
    return (
      <GateShell header="Joining:" onBack={() => navigate('/gate')}>
        <GateNote idx={2}>Entering a competition takes a clan — get yours first</GateNote>
        {/* Role-aware label (Lisa, 2026-07-19): an org member's clan creation
            is an APPLICATION (BuildClanScreen's apply branch) — the pill must
            say so; only founders/platform admins direct-forge. Same
            org-context test BuildClanScreen itself applies. */}
        <GatePill
          label={
            ((me?.orgMemberships?.length ?? 0) > 0 || !!me?.entryOrgId) && me?.role !== 'founder' && me?.role !== 'admin'
              ? 'Request to create a clan'
              : 'Build a clan'
          }
          onClick={() => navigate('/gate/build-clan')}
          primary
          idx={3}
        />
        <GatePill label="Join a clan instead" onClick={() => navigate('/clans/browse')} idx={4} />
      </GateShell>
    );
  }

  // ── In a clan, but not allowed to enter it (2026-07-22, role-visibility audit)
  // "Join a Competition" is a top-level nav item for EVERY role by design
  // (Sidebar.tsx's Home group — top-of-funnel: a newcomer should see something
  // worth taking part in). The clanless branch above already guides them to
  // build/join a clan. The gap this closes: a clan MEMBER who isn't the leader
  // (and holds no captain grant) used to fall straight through to the tappable
  // pill list and 403 on tap — "Only the clan leader can register the clan".
  //
  // Lisa's ruling: inform, don't dead-end. Still show what's on — that's the
  // whole point of the link being open to everyone — but as read-only rows,
  // and point them at the person who can actually enter the clan.
  if (!canEnterComp) {
    return (
      <GateShell header={`Open to ${clanName}:`} onBack={() => navigate('/gate')}>
        {comps.length === 0 ? (
          <GateNote idx={2}>No competitions are open right now — check back soon</GateNote>
        ) : (
          comps.map((c, i) => (
            <CompInfoRow
              key={c.id}
              name={c.name || c.id.replace('COMP#', '')}
              caption={compCaption(c)}
              idx={2 + i}
            />
          ))
        )}
        <GateNote idx={2 + comps.length}>
          Only {clanName}'s leader can enter the clan into a competition
        </GateNote>
        <GatePill label="My clan" onClick={() => navigate('/leader')} primary idx={3 + comps.length} />
      </GateShell>
    );
  }

  if (teamPickStep) {
    return (
      <GateShell header={`Enter ${teamPickStep.compName} as:`} onBack={() => setTeamPickStep(null)}>
        <GatePill
          label="Main team (whole clan)"
          onClick={() => { setSelectedTeamNo(undefined); checkTermsMut.mutate(teamPickStep.compId); setTeamPickStep(null); }}
          disabled={checkTermsMut.isPending}
          // M2: "Main team" stays the default UNLESS the caller is an active
          // captain of one of the named teams — then THEIR team takes the
          // primary slot instead, since that's the fast path for them.
          primary={myTeamNo == null}
          idx={1}
        />
        {teamOptions.map(([teamNoStr, teamName], i) => {
          const isMyTeam = myTeamNo != null && Number(teamNoStr) === myTeamNo;
          return (
            <GatePill
              key={teamNoStr}
              label={teamName}
              caption={isMyTeam ? 'Your team' : undefined}
              onClick={() => { setSelectedTeamNo(Number(teamNoStr)); checkTermsMut.mutate(teamPickStep.compId); setTeamPickStep(null); }}
              disabled={checkTermsMut.isPending}
              primary={isMyTeam}
              idx={2 + i}
            />
          );
        })}
      </GateShell>
    );
  }

  if (acceptStep) {
    return (
      <GateShell header={`Accept ${acceptStep.compName}'s rules:`} onBack={() => setAcceptStep(null)}>
        {acceptStep.rules ? (
          <div className="settle max-h-52 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-white/5 px-4 py-3 text-left font-sans text-[12px] leading-relaxed text-white/75">
            {acceptStep.rules}
          </div>
        ) : (
          <GateNote idx={1}>This competition has no custom rules text — the organiser's standard terms apply.</GateNote>
        )}
        <a
          href={`/comp/${encodeURIComponent(acceptStep.compId)}/rules`}
          target="_blank"
          rel="noreferrer"
          className="settle text-center font-display text-sm font-semibold uppercase tracking-wide text-white/85 underline hover:text-white"
        >
          Read the rules &amp; terms
        </a>
        <label className="settle flex items-center justify-center gap-2.5 px-2 text-center font-display text-[13px] font-medium text-white/85">
          <input
            type="checkbox"
            checked={termsChecked}
            onChange={e => setTermsChecked(e.target.checked)}
            className="h-4 w-4 shrink-0 accent-[var(--accent-500)]"
          />
          I accept the rules and terms on behalf of {clanName}
        </label>
        <GatePill
          label={acceptMut.isPending ? 'Accepting…' : 'Accept & continue'}
          onClick={() => acceptMut.mutate(acceptStep.compId)}
          disabled={!termsChecked || acceptMut.isPending}
          primary
          idx={2}
        />
        {acceptMut.isError && (
          <GateNote tone="error" idx={3}>{friendlyGateError((acceptMut.error as Error).message)}</GateNote>
        )}
      </GateShell>
    );
  }

  return (
    <GateShell header={`Enter ${clanName} into:`} onBack={() => navigate('/gate')}>
      {comps.length === 0 && (
        <GateNote idx={2}>No competitions are open right now — check back soon</GateNote>
      )}
      {comps.map((c, i) => {
        const busyOnThis = (checkTermsMut.isPending && checkTermsMut.variables === c.id)
          || (registerMut.isPending && registerMut.variables?.compId === c.id);
        // registrationOpen is server-derived (see api.ts's Competition type) —
        // undefined (an older cached response, or the DEV preview fixtures
        // above) defaults to open so nothing regresses for those. Caption text
        // itself lives in compCaption so the read-only branch can't drift.
        const closed = c.registrationOpen === false;
        const caption = compCaption(c);
        return (
          <GatePill
            key={c.id}
            label={busyOnThis ? 'Checking…' : (c.name || c.id.replace('COMP#', ''))}
            caption={caption}
            onClick={() => startJoining(c.id, c.name || c.id.replace('COMP#', ''))}
            disabled={closed || checkTermsMut.isPending || registerMut.isPending}
            idx={2 + i}
          />
        );
      })}
      {(checkTermsMut.isError || registerMut.isError) && (
        <GateNote tone="error" idx={2 + comps.length}>
          {friendlyGateError(((registerMut.error ?? checkTermsMut.error) as Error).message)}
        </GateNote>
      )}
    </GateShell>
  );
}
